import { chunkObject, compressData } from "../utils/array";
import api from "./api";
import { NumberFormats } from "../utils/DataTypeUtils";
import { getDefaultCellType } from "../components/process-design/cell/Utils";
import { defaultStyles, defaultThemes } from "../components/process-design/cell/CellConstants";
import { ExtendedRBush } from "./helpers/ExtendedRBush";
import { stateHistory } from "./state-history";
import { helper } from "./Cells";

const allProperties = [
  "bgColor",
  "fontWeight",
  "color",
  "fontStyle",
  "fontSize",
  "textAlign",
  "fontFamily",
  "verticalAlign",
  "dataType",
];

export let tree = new ExtendedRBush(allProperties);

export default {
  namespaced: true,
  state: () => ({}),
  mutations: {},
  actions: {
    load({ state, commit, dispatch }, { data }) {
      if (data) tree = new ExtendedRBush(allProperties).fromJSON(data);
      else tree = new ExtendedRBush(allProperties);
    },
    async persist({ state, commit, dispatch, rootState }) {
      const id = rootState.cells.current.id;
      const sheetName = rootState.sheets.currentSheet.name;
      const data = tree.toJSON();
      const dataChunks = chunkObject(data, 1000); // Adjust based on your needs
      const requestId = `req-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      if (dataChunks.length === 0) dataChunks.push([]); // In case all the cells are deleted.
      try {
        const promises = dataChunks.map((chunk, chunkId) => {
          const payload = {
            sheetName,
            dimension: "styleTree",
            data: chunk,
            chunkId,
            requestId,
            totalChunks: dataChunks.length,
          };
          const compressedData = compressData(payload);
          // Return the promise from api.patch
          return api.patch(`designs/${id}/update-dimensions`, { compressedData });
        });

        // Execute all promises in parallel
        await Promise.all(promises);
      } catch (error) {
        console.error("Error saving bulk changes:", error);
      }
    },
    updateRangeStyle({ commit, state, dispatch, rootState, getters, rootGetters }, { data, customRange = null }) {
      const mainRange = customRange ?? rootGetters["cells/mainRange"].range;
      let reverseArr = [];
      const undoObj = {
        undo: async () => {
          for (const func of reverseArr) {
            await func();
          }
        },
        redo: async () => {
          reverseArr = [];
          reverseArr.push(await helper.style.main.set(data, mainRange));
        },
      };
      stateHistory.snapshot(undoObj);
    },
    clearStyleFormats({ commit, state, dispatch, rootState, getters, rootGetters }, customRange) {
      const range = customRange ?? rootGetters["cells/mainRange"].range;

      let reverseArr = [];
      const undoObj = {
        undo: async () => {
          for (const func of reverseArr) {
            await func();
          }
        },
        redo: async () => {
          reverseArr = [];
          reverseArr.push(await helper.style.main.clear(range));
        },
      };
      stateHistory.snapshot(undoObj);
    },
    addTableStyle({ commit, state, dispatch, rootState, getters, rootGetters }, { data, customRange = null }) {
      const range = customRange ?? rootGetters["cells/mainRange"].range;

      let reverseArr = [];
      const undoObj = {
        undo: async () => {
          for (const func of reverseArr) {
            await func();
          }
        },
        redo: async () => {
          reverseArr = [];
          reverseArr.push(await helper.style.main.addTableStyle(data, range));
        },
      };
      stateHistory.snapshot(undoObj);
    },
    removeTableStyle({ commit, state, dispatch, rootState, getters, rootGetters }, range) {
      range = range ?? rootGetters["cells/mainRange"].range;

      let reverseArr = [];
      const undoObj = {
        undo: async () => {
          for (const func of reverseArr) {
            await func();
          }
        },
        redo: async () => {
          reverseArr = [];
          reverseArr.push(await helper.style.main.removeTableStyle(range));
        },
      };
      stateHistory.snapshot(undoObj);
    },
    updateTableRangeStyle({ commit, state, dispatch, rootState, getters, rootGetters }, { oldRange, newRange, data }) {
      let reverseArr = [];
      const undoObj = {
        undo: async () => {
          for (const func of reverseArr) {
            await func();
          }
        },
        redo: async () => {
          reverseArr = [];
          reverseArr.push(await helper.style.main.updateTableRangeStyle(oldRange, newRange, data));
        },
      };
      stateHistory.snapshot(undoObj);
    },

    moveRangeStyle({ commit, state, dispatch, rootState, getters, rootGetters }, { index, delta, axis, isPositive }) {
      let reverseArr = [];
      const undoObj = {
        undo: async () => {
          for (const func of reverseArr) {
            await func();
          }
          dispatch("cells/toggleRedrawCanvas", null, { root: true });
        },
        redo: async () => {
          reverseArr = [];
          reverseArr.push(await helper.style.main.moveRangeStyle(index, delta, axis, isPositive));
        },
      };
      stateHistory.snapshot(undoObj);
    },
    updateFillStyle({ dispatch }, { initialRange, newRange, isFormatPainter = false }) {
      let reverseArr = [];
      const undoObj = {
        undo: async () => {
          for (const func of reverseArr) {
            await func();
          }
          dispatch("cells/toggleRedrawCanvas", null, { root: true });
        },
        redo: async () => {
          reverseArr = [];
          reverseArr.push(await helper.style.main.updateFillStyle(initialRange, newRange, isFormatPainter));
        },
      };
      stateHistory.snapshot(undoObj);
    },
    //after row and column insertion and deletion this function updates the indexes
    updateItems(
      { commit, state, dispatch, rootState, getters, rootGetters },
      { index, delta, isInsert = false, axis = "Y" }
    ) {
      let reverseArr = [];
      const undoObj = {
        undo: async () => {
          for (const func of reverseArr) {
            await func();
          }
        },
        redo: async () => {
          reverseArr = [];
          reverseArr.push(await helper.style.main.updateItems(index, delta, isInsert, axis));
        },
      };
      stateHistory.snapshot(undoObj);
    },
  },
  getters: {
    dataType: (state, getters, rootState) => (columnIndex, rowIndex) => {
      const dataTypeResult = tree.search({ minX: columnIndex, minY: rowIndex, maxX: columnIndex, maxY: rowIndex });
      if (dataTypeResult.length > 0 && dataTypeResult[0].data.dataType) {
        return dataTypeResult[0].data.dataType;
      } else return NumberFormats.General;
    },
    firstCellStyle: (state) => (columnIndex, rowIndex) => {
      const data = tree.search({ minX: columnIndex, minY: rowIndex, maxX: columnIndex, maxY: rowIndex });
      return data.length > 0 ? data[0]?.data : {};
    },
    properties: (state, getters, rootState) => (columnIndex, rowIndex) => {
      let result = tree.search({ minX: columnIndex, minY: rowIndex, maxX: columnIndex, maxY: rowIndex }) ?? [];

      const row = rootState.cells.current.design.rows[rowIndex];
      const type = rootState.cells.current.type;
      const column = rootState.cells.current.design.columns[columnIndex];
      const cellType = getDefaultCellType(row.type, column.type, type);
      const defaultStyle = defaultStyles[defaultThemes[cellType]] ?? {};
      const obj = Object.assign({}, defaultStyle);
      result = result.sort((a, b) => (a.data.isTable ? -1 : 0));
      result = result.map((item) => {
        if (item.data.isTable) {
          const oddOrEven = (rowIndex - item.minY) % 2;
          return item.data[oddOrEven];
        }
        return item.data;
      });
      return Object.assign(obj, ...result);
    },
  },
};

// Helper function to clear styles by processing non-table ranges and updating the tree

export function arrayToObjectRange(arrayRange, data = {}) {
  return {
    data: { ...data },
    minX: arrayRange[0],
    minY: arrayRange[1],
    maxX: arrayRange[2],
    maxY: arrayRange[3],
  };
}

// Function to convert an object range { minX, minY, maxX, maxY } to an array range
export function objectToArrayRange(objectRange) {
  return [objectRange.minX, objectRange.minY, objectRange.maxX, objectRange.maxY];
}
